import React from 'react';

import { Botao, Chip, Modal, Nota } from '../../../ui';
import { AssociadoDaCarteira, VeiculoDaCarteira } from '../../../api/carteira/carteiraApi';
import { formatCpfCnpj } from '../../../utils/formartCPF';
import { ROTULO_VEICULO_ELETRICO } from '../../../utils/veiculoEletrico';
import { ROTULO_SEM_PLACA } from '../../../utils/veiculoZeroKm';
import {
  pendenciasDaVistoria,
  rotuloEnvioSga,
  rotuloEstadoVistoria,
} from '../../../utils/vistoriaFluxo';
import { totalFotosEsperado } from '../../../utils/vistoriaLegendas';

export interface ModalDetalhesCarteiraProps {
  /** Associado em exibição — null mantém o modal fechado. */
  associado: AssociadoDaCarteira | null;
  /** A tela está ocupada (carregando a lista ou conferindo com o SGA). */
  ocupado: boolean;
  /** Id do veículo cujo laudo está sendo pré-carregado — vira estado no botão. */
  abrindoVistoriaDe?: string | null;
  onFechar: () => void;
  onAdesao: () => void;
  onNovaCotacao: () => void;
  onIniciarVistoria: (veiculo: VeiculoDaCarteira) => void;
  onContinuarVistoria: (veiculo: VeiculoDaCarteira) => void;
  onVerVistoria: (veiculo: VeiculoDaCarteira) => void;
  /** at-81: veículo 0km — abre o "Informar placa". Ausente, o botão não aparece. */
  onInformarPlaca?: (veiculo: VeiculoDaCarteira) => void;
}

/**
 * Detalhes de um associado da carteira (at-32; reescrito na at-65, frente D).
 *
 * Decisão de Victor (31/08/2026, at-32) que ele atende: **a tabela é um índice
 * enxuto**; o estado completo do associado (envio ao SGA, vistoria de cada
 * veículo, produtos, termos, cotações) e **todas as ações** vivem aqui. Nenhuma
 * ação escreve nada — todas levam ao fluxo que já existe.
 *
 * ## O que mudou na at-65
 *
 *   1. **Virou o `Modal` do kit.** O overlay era manual (`.filtros-overlay` +
 *      `.carteira-detalhes-modal`), com listener de `Escape` próprio,
 *      `aria-modal="true"` **sem prender o foco** e um `×` que era caractere
 *      literal. Ganhou foco que entra e volta, Tab circulando dentro e o título
 *      como `<h2>` — exatamente o que a at-63 fez com o modal irmão do painel de
 *      vistoria.
 *   2. 🔴 **Pendência deixou de ser LARANJA.** `.carteira-chip-alerta` pintava
 *      "envio pendente" e "aguardando envio ao SGA" com `#ea5d0e`, **a cor da
 *      marca** — e nada disso é erro nem é da marca: é *"ainda não"* (spec 02
 *      §2.3). Hoje é `Chip pendente` (âmbar), pela mesma regra do painel.
 *   3. 🔴 **O motivo do botão travado saiu do `title`.** "O associado precisa
 *      estar cadastrado no SGA antes da vistoria" e o limite de cotações só
 *      existiam em `title`, que **não existe no celular** — e este app é usado de
 *      pé, na rua (lição da at-54). Agora vão em texto, sob os botões.
 *   4. **A nota do envio automático apareceu.** Ela existia no
 *      `ModalDetalhesAdesao` (a tela irmã) e aqui não: o consultor via chips de
 *      pendência sem nada dizendo que o sistema reenvia sozinho a cada 2 horas.
 *   5. **"Ver vistoria" mostra que está carregando.** O preload
 *      (`getUltimaVistoriaPorVeiculo` antes de navegar, para o laudo não abrir
 *      vazio) rodava sob um `FullScreenLoader` de tela cheia; virou estado do
 *      botão **daquele** veículo.
 *   6. **Motocicleta elétrica é dita por extenso** (at-46/at-54): a "placa"
 *      `BIK0001` é uma identificação gerada pelo SAAC, e a carteira era a última
 *      tela que ainda a exibia como se fosse placa de carro.
 *
 * ⚠️ **A regra de texto+cor do estado da vistoria NÃO mora aqui** — vem de
 * `utils/vistoriaFluxo.ts`, a mesma fonte do painel `/vistoria` e do modal dele.
 * O comentário de `rotuloEstadoVistoria` já previa este consumidor em tantas
 * palavras: as duas telas **nunca podem divergir sobre o mesmo veículo**.
 *
 * ⚠️ **A oficina saiu deste modal na at-53** (Victor, 08/09/2026: *"nada de
 * oficina na carteira"*) e não volta: estado de integração, chip de etapa e
 * comentários vivem na tela Acompanhamento de Oficina.
 */
const ModalDetalhesCarteira: React.FC<ModalDetalhesCarteiraProps> = ({
  associado,
  ocupado,
  abrindoVistoriaDe = null,
  onFechar,
  onAdesao,
  onNovaCotacao,
  onIniciarVistoria,
  onContinuarVistoria,
  onVerVistoria,
  onInformarPlaca,
}) => {
  if (!associado) return null;

  const limiteAtingido = associado.cotacoesNoMes >= associado.limiteCotacoesMes;

  return (
    <Modal
      aberto
      titulo={associado.nomeCompleto}
      descricao={formatCpfCnpj(String(associado.cpfCnpj || ''))}
      tamanho="lg"
      /* ⚠️ `ocupado` do `Modal` NÃO recebe o `ocupado` da tela. Ele trava Esc,
         clique fora e o botão de fechar, e existe para "há uma gravação em
         andamento DENTRO deste diálogo" — aqui o que está ocupado é a lista, ao
         fundo. Prender o consultor num modal porque a tela recarregou seria o
         defeito, não a proteção. O `ocupado` desabilita as AÇÕES, que é o que ele
         sempre fez nesta tela. */
      aoFechar={onFechar}
    >
      <div className="tw-flex tw-flex-col tw-gap-6">
        <section>
          <h3 className="tw-m-0 tw-mb-2 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
            Associado
          </h3>

          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {/* A origem é POR REGISTRO (at-32): fica no modal, nunca na linha da
                tabela — lá ela sugeriria que vale para o associado e todos os
                veículos dele, e um associado importado pode ter veículo criado
                aqui (e vice-versa). */}
            {associado.origem === 'SGA_IMPORTADO' && (
              <Chip variante="neutro">Importado do SGA</Chip>
            )}

            <Chip variante={associado.addedToSga ? 'ok' : 'pendente'}>
              {associado.addedToSga ? 'Cadastrado no SGA' : 'Envio ao SGA pendente'}
            </Chip>

            <Chip variante={limiteAtingido ? 'pendente' : 'neutro'}>
              Cotações no mês: {associado.cotacoesNoMes}/{associado.limiteCotacoesMes}
            </Chip>
          </div>

          {/* O envio é automático — dizer isso evita que pendência vire susto. É a
              mesma nota do `ModalDetalhesAdesao` (at-54) e da legenda do painel de
              vistoria (at-63); aqui ela simplesmente não existia. */}
          <Nota className="tw-mt-3">
            O envio ao SGA é <strong className="tw-font-bold">automático</strong>: o sistema
            tenta na hora e repete a cada 2 horas. Pendência aqui não exige ação sua.
          </Nota>

          <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row">
            <Botao variante="secundario" disabled={ocupado} onClick={onAdesao}>
              Ver em Adesões
            </Botao>
            <Botao
              variante="secundario"
              disabled={ocupado || limiteAtingido}
              onClick={onNovaCotacao}
            >
              Nova cotação
            </Botao>
          </div>

          {limiteAtingido && (
            /* 🔴 O motivo em TEXTO, não só no `title` — ver o item 3 do cabeçalho. */
            <p className="tw-mb-0 tw-mt-2 tw-text-xs tw-text-amber-deep">
              Este CPF já atingiu o limite de {associado.limiteCotacoesMes} cotações no mês.
            </p>
          )}
        </section>

        <section>
          <h3 className="tw-m-0 tw-mb-2 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
            {associado.veiculos.length === 1
              ? 'Veículo'
              : `Veículos (${associado.veiculos.length})`}
          </h3>

          {associado.veiculos.length === 0 ? (
            <p className="tw-m-0 tw-rounded-xl tw-border tw-border-dashed tw-border-texto-desabilitado/50 tw-px-4 tw-py-6 tw-text-center tw-text-sm tw-text-texto-suave">
              Nenhum veículo cadastrado para este associado
            </p>
          ) : (
            <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-3 tw-p-0">
              {associado.veiculos.map((veiculo) => (
                <CardVeiculo
                  key={veiculo.id}
                  veiculo={veiculo}
                  ocupado={ocupado}
                  abrindoVistoria={abrindoVistoriaDe === veiculo.id}
                  onIniciarVistoria={onIniciarVistoria}
                  onContinuarVistoria={onContinuarVistoria}
                  onVerVistoria={onVerVistoria}
                  onInformarPlaca={onInformarPlaca}
                />
              ))}
            </ul>
          )}
        </section>
      </div>
    </Modal>
  );
};

interface PropsCardVeiculo {
  veiculo: VeiculoDaCarteira;
  ocupado: boolean;
  abrindoVistoria: boolean;
  onIniciarVistoria: (veiculo: VeiculoDaCarteira) => void;
  onContinuarVistoria: (veiculo: VeiculoDaCarteira) => void;
  onVerVistoria: (veiculo: VeiculoDaCarteira) => void;
  onInformarPlaca?: (veiculo: VeiculoDaCarteira) => void;
}

/**
 * Um veículo do associado.
 *
 * ⚠️ **Veículo substituído continua aqui, como histórico** (at-37, item 39) — antes
 * ele sumia da carteira e ficava indistinguível de uma exclusão. O que ele perde
 * são as ações de vistoria: "Ver vistoria" fica (é leitura), "Vistoriar" e
 * "Continuar" somem. Deixá-los desabilitados exibiria o motivo **errado** — a
 * trava não é o associado fora do SGA, é o veículo estar fora de circulação.
 */
const CardVeiculo: React.FC<PropsCardVeiculo> = ({
  veiculo,
  ocupado,
  abrindoVistoria,
  onIniciarVistoria,
  onContinuarVistoria,
  onVerVistoria,
  onInformarPlaca,
}) => {
  const { vistoria } = veiculo;
  const emAndamento = !!vistoria && !vistoria.concluida;
  const estado = rotuloEstadoVistoria(veiculo);

  const pendencias = vistoria
    ? pendenciasDaVistoria(vistoria.proximoPasso, vistoria.quantidadeFotos, veiculo.tipoVeiculo)
    : [];

  return (
    <li
      className={[
        'tw-rounded-xl tw-border tw-border-l-4 tw-border-superficie-borda tw-p-4',
        // O substituído recua visualmente: barra âmbar e fundo neutro, para não
        // competir com os veículos em circulação.
        veiculo.substituido
          ? 'tw-border-l-amber-warn tw-bg-superficie'
          : 'tw-border-l-brand tw-bg-white',
      ].join(' ')}
    >
      <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-2">
        <div className="tw-min-w-0">
          <p className="tw-m-0 tw-font-marca tw-text-base tw-font-bold tw-tracking-wider tw-text-texto-titulo [overflow-wrap:anywhere]">
            {/* at-81: a "placa" do 0km é a identificação provisória pelo chassi —
                exibida como texto, não como uma placa estranha. */}
            {veiculo.zeroKm ? ROTULO_SEM_PLACA : veiculo.placa || '—'}
          </p>
          <p className="tw-mb-0 tw-mt-0.5 tw-text-xs tw-text-texto-suave">
            {/* at-46/at-54: a "placa" da motocicleta elétrica é a identificação
                gerada pelo SAAC — dizer o tipo evita o consultor achar que é uma
                placa de verdade. Mesmo texto do painel de vistoria. */}
            {veiculo.bikeEletrica ? `${ROTULO_VEICULO_ELETRICO} · ` : ''}
            {veiculo.modelo || '—'}
          </p>
        </div>

        <div className="tw-flex tw-flex-wrap tw-justify-end tw-gap-1.5">
          {veiculo.substituido && <Chip variante="pendente">Substituído</Chip>}
          {veiculo.zeroKm && !veiculo.substituido && (
            <Chip variante="pendente">Aguardando placa</Chip>
          )}
          {veiculo.origem === 'SGA_IMPORTADO' && <Chip variante="neutro">Importado do SGA</Chip>}
        </div>
      </div>

      <div className="tw-mt-3 tw-flex tw-flex-wrap tw-gap-1.5">
        <Chip variante={veiculo.addedToSga ? 'ok' : 'pendente'}>
          {veiculo.addedToSga ? 'Veículo no SGA' : 'Envio ao SGA pendente'}
        </Chip>
        <Chip variante="neutro">{veiculo.produtos} produto(s)</Chip>
        <Chip variante="neutro">{veiculo.termosLocais} termo(s)</Chip>
      </div>

      {veiculo.zeroKm && !veiculo.substituido && onInformarPlaca && (
        <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
          <p className="tw-m-0 tw-text-xs tw-text-texto-suave">
            Veículo 0km, identificado pelo chassi até ser emplacado.
          </p>
          <Botao variante="primario" tamanho="sm" disabled={ocupado} onClick={() => onInformarPlaca(veiculo)}>
            Informar placa
          </Botao>
        </div>
      )}

      <div className="tw-mt-3 tw-border-t tw-border-superficie-borda tw-pt-3">
        <h4 className="tw-m-0 tw-mb-2 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
          Vistoria
        </h4>

        <Chip variante={estado.variante}>{estado.texto}</Chip>

        {vistoria?.concluida && (
          <p className="tw-mb-0 tw-mt-2 tw-text-xs tw-text-texto-suave">
            {vistoria.quantidadeFotos} foto(s) · assinada
          </p>
        )}

        {/* Vistoria existe no banco mas o fluxo não terminou (as fotos sobem antes
            do checklist, at-24) — a contagem de fotos entra nas pendências. */}
        {emAndamento && vistoria && (
          <p className="tw-mb-0 tw-mt-2 tw-text-xs tw-text-texto-suave">
            {vistoria.quantidadeFotos} de {totalFotosEsperado(veiculo.tipoVeiculo)} foto(s)
            {pendencias.length > 0 && <> · falta {pendencias.join(', ')}</>}
          </p>
        )}

        {vistoria && (
          <div className="tw-mt-3 tw-flex tw-flex-wrap tw-gap-1.5">
            {([
              rotuloEnvioSga(vistoria.laudoEnviadoSga, 'Laudo'),
              rotuloEnvioSga(vistoria.fotosEnviadasSga, 'Fotos'),
            ] as const).map((chip) => (
              <Chip key={chip.texto} variante={chip.variante}>
                {chip.texto}
              </Chip>
            ))}
          </div>
        )}

        <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row sm:tw-justify-end">
          {vistoria?.concluida && (
            <Botao
              variante="secundario"
              tamanho="sm"
              carregando={abrindoVistoria}
              disabled={ocupado}
              onClick={() => onVerVistoria(veiculo)}
            >
              Ver vistoria
            </Botao>
          )}

          {!veiculo.substituido &&
            (emAndamento ? (
              <Botao
                variante="primario"
                tamanho="sm"
                disabled={ocupado}
                onClick={() => onContinuarVistoria(veiculo)}
              >
                Continuar vistoria
              </Botao>
            ) : (
              <Botao
                variante="primario"
                tamanho="sm"
                disabled={ocupado || !veiculo.podeVistoriar}
                onClick={() => onIniciarVistoria(veiculo)}
              >
                {vistoria ? 'Nova vistoria' : 'Vistoriar'}
              </Botao>
            ))}
        </div>

        {!veiculo.substituido && !emAndamento && !veiculo.podeVistoriar && (
          /* 🔴 O motivo em TEXTO — ver o item 3 do cabeçalho. Mesmo texto do
             `ModalDetalhesVistoria` (at-63), para as duas portas de entrada do
             fluxo dizerem a mesma coisa. */
          <p className="tw-mb-0 tw-mt-2 tw-text-xs tw-text-amber-deep">
            Para vistoriar, o associado precisa estar cadastrado no SGA. O envio é
            automático e o sistema tenta a cada 2 horas.
          </p>
        )}
      </div>
    </li>
  );
};

export default ModalDetalhesCarteira;
